import * as React from "react";
import { useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { RfqSearchResult, searchRfqs } from "../api/ProcurementClient";
import { getRFQStatusLabel } from "../models/RFQStatus";
import { QUEUE_URL } from "../MainAppPanel";

const SEARCH_DEBOUNCE_MS = 250;

/** RFQ # / PO # lookup available from every page; shows matches on the Work Queue. */
const GlobalRfqSearch: React.FC = () => {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<RfqSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const q = inputValue.trim();
    if (q.length < 2) {
      setOptions([]);
      setLoading(false);
      setError(null);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    const timer = window.setTimeout(() => {
      searchRfqs(q, { signal: controller.signal })
        .then((results) => {
          setOptions(results);
          setError(null);
        })
        .catch((err) => {
          if ((err as { code?: string }).code === "ERR_CANCELED") return;
          setOptions([]);
          setError(err instanceof Error ? err.message : "Search failed");
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [inputValue]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  /** Show the match(es) on the Work Queue, filtered by the RFQ # / PO #. */
  const showInWorkQueue = (term: string) => {
    const q = term.trim();
    if (!q) return;
    setInputValue("");
    setOptions([]);
    setOpen(false);
    inputRef.current?.blur();
    navigate(`${QUEUE_URL}?${new URLSearchParams({ q }).toString()}`);
  };

  /** Picking a result filters to the number the user was after. */
  const termForResult = (result: RfqSearchResult) => {
    const needle = inputValue.trim().toLowerCase();
    const matchedPo = result.poNumbers.find((po) =>
      po.toLowerCase().includes(needle)
    );
    return matchedPo && !result.referenceNum.toLowerCase().includes(needle)
      ? matchedPo
      : result.referenceNum;
  };

  return (
    <Autocomplete<RfqSearchResult, false, false, true>
      freeSolo
      size="small"
      open={open && inputValue.trim().length >= 2}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      loading={loading}
      filterOptions={(x) => x}
      inputValue={inputValue}
      onInputChange={(_event, value, reason) => {
        if (reason !== "reset") setInputValue(value);
      }}
      value={null}
      onChange={(_event, value) => {
        if (value && typeof value !== "string") {
          showInWorkQueue(termForResult(value));
        } else if (typeof value === "string") {
          showInWorkQueue(value);
        }
      }}
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option.referenceNum
      }
      isOptionEqualToValue={(a, b) => a.id === b.id}
      noOptionsText={error ?? "No RFQ or PO # matches"}
      loadingText="Searching…"
      sx={{ width: { xs: "100%", sm: 340 } }}
      renderOption={(props, option) => {
        return (
          <Box component="li" {...props} key={option.id}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600} noWrap>
                {option.referenceNum}
                <Typography
                  component="span"
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: 1 }}
                >
                  {getRFQStatusLabel(option.status)}
                </Typography>
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {[
                  option.customerName,
                  option.poNumbers.length
                    ? `PO ${option.poNumbers.join(", ")}`
                    : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </Typography>
            </Box>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={inputRef}
          placeholder="Search RFQ # / PO #"
          slotProps={{
            input: {
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
          sx={{ bgcolor: "white" }}
        />
      )}
    />
  );
};

export default GlobalRfqSearch;
